import { fireInteractionEvent } from 'api/googleTagManager'
import { trackEvent } from 'analytics/analytics'
import {
  flightsBusinessFareClassSelect,
  flightsEconomyFareClassSelect,
  flightsFirstFareClassSelect,
  flightsFlightSearchClick,
  flightsOneWayFareTypeSelect,
  flightsPremiumFareClassSelect,
  flightsReturnFareTypeSelect,
} from 'analytics/eventDefinitions'
import { mapFlightSearchDataToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import { searchEventWithContext } from 'analytics/snowplow/events'
import { FlightsClassTypes, FlightsFareTypes, fareClassToUrlMap } from 'constants/flight'
import type { FlightSearchWidgetState } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import type { FlightSearchWidgetFlightItem } from 'contexts/Flights/FlightSearchWidget/types'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { countOccupantsForFlights } from 'lib/offer/occupancyUtils'
import moment from 'moment'
import qs from 'qs'
import type { Filter_1_0_0 } from '@luxuryescapes/contract-data-event-schemas/lib/com.luxgroup'
import { getFlightSearchQueryparams, setFlightSessionRecentSearch } from './flightUtils'

export const FLIGHTS_SEARCH_RESULTS_PATH = '/flights-search-results'
export const FLIGHTS_SEARCH_RESULTS_MULTI_CITY_PATH = '/flights-search-results-multi-city'

export interface FlightSearchSubmissionContext {
  previousFlights?: Array<FlightSearchWidgetFlightItem>
  filterAirlines?: Array<string>
  dealId?: string
  source?: string | null
  departingQuotedPrice?: string | null
  returningQuotedPrice?: string | null
  isFlightsCredit?: boolean
  creditCarrier?: string
}

export function resolveSearchFareType(state: FlightSearchWidgetState): FlightsFareTypes {
  const fareType = state.fareType?.value as FlightsFareTypes

  if (fareType === FlightsFareTypes.MULTI_CITY && state.flights.length === 1) {
    return FlightsFareTypes.ONE_WAY
  }

  return fareType
}

export function haveAirportsChanged(
  flights: Array<FlightSearchWidgetFlightItem>,
  previousFlights?: Array<FlightSearchWidgetFlightItem>,
): boolean {
  return flights.some((flight, index) => {
    const previousFlight = previousFlights?.[index]

    return (
      flight.departureAirport?.airportCode !== previousFlight?.departureAirport?.airportCode ||
      flight.arrivalAirport?.airportCode !== previousFlight?.arrivalAirport?.airportCode
    )
  })
}

export function buildFlightSearchNavigation(
  state: FlightSearchWidgetState,
  context: FlightSearchSubmissionContext = {},
) {
  const { flights, fareCabin } = state
  const fareType = resolveSearchFareType(state)
  const airportsChanged = haveAirportsChanged(flights, context.previousFlights)
  const occupants = countOccupantsForFlights([state.occupants])

  const dealId = context.dealId ?? state.flightDealId
  const shouldResetDealId = !!state.flightDealId && airportsChanged

  const params = {
    adults: occupants.adults,
    children: occupants.children,
    infants: occupants.infants,
    childrenAge: occupants.childrenAge.join(','),
    fareClass: fareCabin?.value ? fareClassToUrlMap[fareCabin.value] : undefined,
    fareType,
    searchId: moment().unix(),
    flightsCredit: context.isFlightsCredit || undefined,
    carrier: context.isFlightsCredit ? context.creditCarrier : undefined,
    dealId: shouldResetDealId ? undefined : dealId,
    ...(!airportsChanged ? { filterAirlines: context.filterAirlines?.join(',') } : {}),
    ...(context.source && { source: context.source }),
  }

  const flightParams = getFlightSearchQueryparams(flights, fareType)

  if (fareType === FlightsFareTypes.MULTI_CITY) {
    return {
      path: FLIGHTS_SEARCH_RESULTS_MULTI_CITY_PATH,
      query: qs.stringify({ ...params, ...flightParams }),
      airportsChanged,
      shouldResetDealId,
    }
  }

  return {
    path: FLIGHTS_SEARCH_RESULTS_PATH,
    query: qs.stringify({
      ...params,
      ...flightParams,
      ...(fareCabin?.value === FlightsClassTypes.ECONOMY ? {
        departingQuotedPrice: airportsChanged ? flightParams.departingQuotedPrice : flightParams.departingQuotedPrice ?? context.departingQuotedPrice,
        returningQuotedPrice: airportsChanged ? flightParams.returningQuotedPrice : flightParams.returningQuotedPrice ?? context.returningQuotedPrice,
      } : {
        departingQuotedPrice: 0,
        returningQuotedPrice: 0,
      }),
    }),
    airportsChanged,
    shouldResetDealId,
  }
}

export function trackFlightSearch(
  state: FlightSearchWidgetState,
  filterContexts?: Array<Filter_1_0_0>,
) {
  const { flights, fareCabin, occupants } = state
  const fareType = resolveSearchFareType(state)
  const isReturnFare = state.fareType?.value === FlightsFareTypes.RETURN
  const transformedOccupants = countOccupantsForFlights([occupants])

  flights.forEach(flight => {
    const departDate = moment(flight.checkinDate).format(ISO_DATE_FORMAT)
    const returnDate = isReturnFare ? moment(flight.checkoutDate).format(ISO_DATE_FORMAT) : undefined

    trackEvent(searchEventWithContext({
      searchEvent: { type: 'search_bar' },
      sortContext: { value: 'recommended' },
      filterContexts,
      searchContext: mapFlightSearchDataToSnowplowSearchEvent({
        searchVerticals: new Set(['flights']),
        originAirportCode: flight.departureAirport?.airportCode,
        originAirportName: flight.departureAirport?.airportName,
        destinationAirportCode: flight.arrivalAirport?.airportCode,
        destinationAirportName: flight.arrivalAirport?.airportName,
        departDate,
        returnDate,
        occupancies: transformedOccupants,
        fareClass: fareCabin?.value,
        fareType,
      }),
    }))
  })

  fireInteractionEvent(flightsFlightSearchClick())

  setFlightSessionRecentSearch({
    fareType,
    fareClass: fareCabin?.value,
    occupants,
    flights: flights.map(flight => ({
      departingDate: moment(flight.checkinDate).format(ISO_DATE_FORMAT),
      returningDate: isReturnFare ? moment(flight.checkoutDate).format(ISO_DATE_FORMAT) : null,
      departureAirport: flight.departureAirport,
      arrivalAirport: flight.arrivalAirport,
    })),
  })
}

export function trackFareTypeSelect(fareType: FlightsFareTypes) {
  if (fareType === FlightsFareTypes.ONE_WAY) {
    fireInteractionEvent(flightsOneWayFareTypeSelect())
  } else if (fareType === FlightsFareTypes.RETURN) {
    fireInteractionEvent(flightsReturnFareTypeSelect())
  }
}

export function trackFareCabinSelect(fareCabin: FlightsClassTypes) {
  switch (fareCabin) {
    case FlightsClassTypes.ECONOMY:
      fireInteractionEvent(flightsEconomyFareClassSelect())
      break
    case FlightsClassTypes.PREMIUM:
      fireInteractionEvent(flightsPremiumFareClassSelect())
      break
    case FlightsClassTypes.BUSINESS:
      fireInteractionEvent(flightsBusinessFareClassSelect())
      break
    case FlightsClassTypes.FIRST:
      fireInteractionEvent(flightsFirstFareClassSelect())
      break
    default:
      break
  }
}
